import { useTranslation } from 'react-i18next';
import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

/**
 * FloatingActions — the same buttons a screen already has in its footer or
 * toolbar, mirrored as icon-only circles pinned to the bottom-right.
 *
 * On a long form or a long list the real buttons scroll out of reach; this
 * strip keeps Save / Approve / Export one thumb away without covering the
 * fields (it sits in a zero-height sticky row, pointer-events off except
 * on the buttons themselves). Close is deliberately NOT included — every
 * dialog already has its X in the top-right corner.
 *
 * Jump-to-top / jump-to-bottom arrows are added automatically whenever the
 * page is long enough to scroll, so no screen has to wire them up. Inside a
 * dialog the page itself does not scroll, so they stay hidden — the dialog
 * carries its own pair next to its close button.
 *
 * actions: [{ icon, label, onClick, disabled, tone, testId, hidden }]
 *   tone: 'primary' (brand red) | 'default' (white) | 'success'
 */
const TONES = {
  primary: 'bg-[#AA0000] text-white hover:bg-[#880000] ring-black/10',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 ring-black/10',
  default: 'bg-white text-slate-700 hover:bg-slate-50 ring-slate-300',
};

const BTN = 'pointer-events-auto inline-flex items-center justify-center rounded-full shadow-lg '
  + 'shadow-black/25 ring-1 transition-[background-color,box-shadow,transform] duration-150 '
  + 'hover:shadow-xl active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#AA0000] '
  + 'focus:ring-offset-2 disabled:opacity-40 disabled:pointer-events-none ';

const usePageScroll = (ref) => {
  const { t } = useTranslation();
  const [state, setState] = React.useState({ scrollable: false, atTop: true, atBottom: false });

  React.useEffect(() => {
    // Inside a dialog the page behind is locked and the dialog carries its
    // own pair of arrows, so this strip stays out of it.
    if (ref.current?.closest('[role="dialog"]')) return;
    const measure = () => {
      const doc = document.documentElement;
      const slack = doc.scrollHeight - window.innerHeight;
      setState({
        scrollable: slack > 160,
        atTop: window.scrollY <= 8,
        atBottom: window.scrollY >= slack - 8,
      });
    };
    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [ref]);

  return state;
};

export const FloatingActions = ({ actions = [] }) => {
  const { t } = useTranslation();
  const hostRef = React.useRef(null);
  const { scrollable, atTop, atBottom } = usePageScroll(hostRef);
  const shown = actions.filter((a) => a && !a.hidden && a.icon);
  if (shown.length === 0 && !scrollable) return <span ref={hostRef} className="hidden" />;

  const jump = (top) => window.scrollTo({ top, behavior: 'smooth' });

  return (
    <div ref={hostRef} className="sticky bottom-0 z-40 !mt-16 h-0 flex justify-end items-end pointer-events-none">
      <div className="flex items-center gap-2 mb-1 mr-1">
        {scrollable && (
          <div className="flex items-center gap-1.5">
            <button
              type="button" onClick={() => jump(0)} disabled={atTop}
              title={t('auto.misc2.scrollToTop')} aria-label={t('auto.misc2.scrollToTop')} data-testid="page-scroll-top"
              className={`${BTN} h-9 w-9 ${TONES.default}`}
            >
              <ArrowUp className="h-4 w-4" />
            </button>
            <button
              type="button" onClick={() => jump(document.documentElement.scrollHeight)} disabled={atBottom}
              title={t('auto.misc2.scrollToBottom')} aria-label={t('auto.misc2.scrollToBottom')} data-testid="page-scroll-bottom"
              className={`${BTN} h-9 w-9 ${TONES.default}`}
            >
              <ArrowDown className="h-4 w-4" />
            </button>
          </div>
        )}
        {shown.map(({ icon: Icon, label, onClick, disabled, tone = 'default', testId }) => (
          <button
            key={testId || label}
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={label}
            aria-label={label}
            data-testid={testId}
            className={`${BTN} h-11 w-11 ${TONES[tone] || TONES.default}`}
          >
            <Icon className="h-5 w-5" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default FloatingActions;
